// src/services/profileSubmitter.js

// --- [MODIFICATION] --- Added imports for education and experience
import {
  updateUserProfile,
  getOrCreateSkill,
  addUserSkill,
  getOrCreateInstitution,
  addEducationRecord,
  addExperience,
} from "./api";
import { mapEducationData, mapExperienceData } from "./apiHelpers";

/**
 * A comprehensive function to save the entire user profile from the wizard.
 * It handles updating the main profile, education, experience, and skills.
 *
 * @param {object} formData - The complete formData object from ProfileContext.
 * @param {object} parsedResumeData - Optional parsed resume data from AI
 */
export const saveFullProfile = async (formData, parsedResumeData = null) => {
  console.log("[profileSubmitter] Starting full profile save. Data:", formData);
  console.log("[profileSubmitter] Parsed resume data:", parsedResumeData);

  // --- Step 1: Prepare and save the main UserProfile data ---
  // This includes account info, preferences, licenses, and tools.
  const profilePayload = {
    // from account section
    full_name: `${formData.account.firstName || ""} ${
      formData.account.lastName || ""
    }`.trim(),
    headline: formData.account.headline,
    bio: formData.account.bio,
    location: formData.account.location,
    phone_number: formData.account.phoneNumber,
    time_zone: formData.account.timeZone,
    profession: formData.account.profession,

    // from preferences section
    preferred_job_titles: (formData.preferences.desiredJobTitles || [])
      .map((s) => s.trim())
      .filter(Boolean),
    preferred_industries: formData.preferences.preferredIndustries,
    salary_expectation: formData.preferences.minSalary
      ? parseFloat(formData.preferences.minSalary)
      : null,
    work_arrangement: formData.preferences.workArrangement,
    company_size: formData.preferences.companySize,
    work_values: formData.preferences.values,
    deal_breakers: formData.preferences.dealBreakers || [],

    // from skills section
    licenses: formData.skills.licenses || [],
    tools_software: formData.skills.tools || [],

    // from skillTest section
    ...formData.skillTest, // Spread all skillTest answers into the main payload
  };

  try {
    console.log(
      "[profileSubmitter] Updating main user profile with payload:",
      profilePayload
    );
    await updateUserProfile(profilePayload);
    console.log("[profileSubmitter] Main profile updated successfully.");
  } catch (error) {
    console.error(
      "[profileSubmitter] Failed to update main user profile:",
      error
    );
    throw new Error(`Failed to save profile details: ${error.message}`);
  }

  // --- Step 2: Save Education Records ---
  // Process education from both formData and parsedResumeData
  try {
    const educationsToSave =
      parsedResumeData?.education || formData.educations || [];

    if (educationsToSave.length > 0) {
      console.log(
        "[profileSubmitter] Saving education records:",
        educationsToSave
      );

      for (const education of educationsToSave) {
        try {
          console.log("[profileSubmitter] Processing education:", education);

          // Get or create institution
          const institutionName =
            education.institutionName || education.institution?.name;
          if (!institutionName) {
            console.warn(
              "[profileSubmitter] Skipping education without institution name"
            );
            continue;
          }

          const institution = await getOrCreateInstitution({
            name: institutionName,
          });

          console.log(
            "[profileSubmitter] Institution found/created:",
            institution
          );

          // Prepare education data
          const educationData = {
            institution_id: institution.id,
            degree: education.degree,
            fieldOfStudy: education.fieldOfStudy || education.field_of_study,
            startDate: education.startDate || education.start_date,
            endDate: education.endDate || education.end_date,
            description: education.description || "", // CRITICAL: Include description
          };

          console.log(
            "[profileSubmitter] Education data before mapping:",
            educationData
          );

          // Map to API format
          const mappedEducation = mapEducationData(educationData);

          console.log(
            "[profileSubmitter] Mapped education data:",
            mappedEducation
          );
          console.log(
            "[profileSubmitter] Description being saved:",
            mappedEducation.description
          );

          // Save education record
          const savedEducation = await addEducationRecord(mappedEducation);
          console.log("[profileSubmitter] Saved education:", savedEducation);
        } catch (eduError) {
          console.error(
            "[profileSubmitter] Failed to save education record:",
            eduError
          );
          // Continue with next education
        }
      }

      console.log("[profileSubmitter] Education records saved successfully.");
    }
  } catch (error) {
    console.error(
      "[profileSubmitter] Failed to save education records:",
      error
    );
    // Log and continue
  }

  // --- Step 3: Save Experience Records ---
  // Process experience from both formData and parsedResumeData
  try {
    const experiencesToSave =
      parsedResumeData?.experience || formData.experiences || [];

    if (experiencesToSave.length > 0) {
      console.log(
        "[profileSubmitter] Saving experience records:",
        experiencesToSave
      );

      for (const experience of experiencesToSave) {
        try {
          // Prepare experience data - handle both AI and form field names
          const experienceData = {
            company: experience.company || experience.companyName,
            title: experience.title || experience.jobTitle,
            location: experience.location,
            employmentType:
              experience.employmentType ||
              experience.employment_type ||
              "Full-time",
            startDate: experience.startDate || experience.start_date,
            endDate: experience.endDate || experience.end_date,
            description: experience.description || "",
            achievements: experience.achievements || [],
            responsibilities: experience.responsibilities || [],
          };

          // Map to API format
          const mappedExperience = mapExperienceData(experienceData);

          console.log(
            "[profileSubmitter] Mapped experience data:",
            mappedExperience
          );

          // Save experience record
          const savedExperience = await addExperience(mappedExperience);
          console.log("[profileSubmitter] Saved experience:", savedExperience);
        } catch (expError) {
          console.error(
            "[profileSubmitter] Failed to save experience record:",
            expError
          );
          // Continue with next experience
        }
      }

      console.log("[profileSubmitter] Experience records saved successfully.");
    }
  } catch (error) {
    console.error(
      "[profileSubmitter] Failed to save experience records:",
      error
    );
    // Log and continue
  }

  // --- Step 4: Save Skills ---
  // This handles the 'skills' array, which needs to be linked via the user_skills table.
  const skillsToSave =
    parsedResumeData?.skills_and_profile?.skills ||
    formData.skills.skills ||
    [];
  try {
    if (skillsToSave.length > 0) {
      console.log("[profileSubmitter] Saving skills:", skillsToSave);
      await Promise.all(
        skillsToSave.map(async (skillName) => {
          try {
            // Find or create the master skill record
            const skill = await getOrCreateSkill(skillName, "Technical Skill");
            // If successful, create the link to the user
            if (skill && skill.id) {
              await addUserSkill({ skill_id: skill.id });
            }
          } catch (skillError) {
            console.error(
              `[profileSubmitter] Failed to save skill '${skillName}':`,
              skillError
            );
          }
        })
      );
      console.log("[profileSubmitter] Skills saved successfully.");
    }
  } catch (error) {
    console.error("[profileSubmitter] Failed to save skills:", error);
    // Log and continue. The main profile was saved, which is most important.
  }

  console.log("[profileSubmitter] Full profile save process completed.");
};

/**
 * Helper function specifically for processing resume upload data
 * @param {object} parsedResumeData - Data parsed from AI
 */
export const processResumeUploadData = async (parsedResumeData) => {
  console.log(
    "[profileSubmitter] Processing resume upload data:",
    parsedResumeData
  );

  const results = {
    profile: false,
    education: [],
    experience: [],
    skills: [],
  };

  try {
    // Update basic profile info
    if (parsedResumeData.skills_and_profile) {
      const profileUpdate = {
        full_name: parsedResumeData.skills_and_profile.full_name || "",
        phone_number: parsedResumeData.skills_and_profile.phone_number || "",
        location: parsedResumeData.skills_and_profile.location || "",
        headline: parsedResumeData.skills_and_profile.headline || "",
      };

      await updateUserProfile(profileUpdate);
      results.profile = true;
    }

    // Process education with descriptions
    if (parsedResumeData.education && parsedResumeData.education.length > 0) {
      for (const edu of parsedResumeData.education) {
        try {
          console.log(
            "[profileSubmitter] Processing education with description:",
            edu.description
          );

          // Get or create institution
          const institution = await getOrCreateInstitution({
            name: edu.institutionName,
          });

          // Prepare and save education
          const educationData = {
            institution_id: institution.id,
            degree: edu.degree,
            fieldOfStudy: edu.fieldOfStudy,
            startDate: edu.startDate,
            endDate: edu.endDate,
            description: edu.description || "", // Ensure description is included
          };

          const mappedEducation = mapEducationData(educationData);
          const savedEducation = await addEducationRecord(mappedEducation);

          results.education.push(savedEducation);
          console.log(
            "[profileSubmitter] Saved education with description:",
            savedEducation.description
          );
        } catch (error) {
          console.error("[profileSubmitter] Failed to save education:", error);
        }
      }
    }

    // Process experience
    if (parsedResumeData.experience && parsedResumeData.experience.length > 0) {
      for (const exp of parsedResumeData.experience) {
        try {
          const experienceData = {
            companyName: exp.company,
            jobTitle: exp.title,
            location: exp.location,
            employmentType: exp.employmentType || "Full-time",
            startDate: exp.startDate,
            endDate: exp.endDate,
            description: exp.description || "",
            achievements: exp.achievements || [],
            responsibilities: exp.responsibilities || [],
          };

          const mappedExperience = mapExperienceData(experienceData);
          const savedExperience = await addExperience(mappedExperience);

          results.experience.push(savedExperience);
        } catch (error) {
          console.error("[profileSubmitter] Failed to save experience:", error);
        }
      }
    }

    // Process skills
    if (parsedResumeData.skills_and_profile?.skills) {
      for (const skillName of parsedResumeData.skills_and_profile.skills) {
        try {
          const skill = await getOrCreateSkill(skillName, "Technical Skill");
          if (skill && skill.id) {
            await addUserSkill({ skill_id: skill.id });
            results.skills.push(skill);
          }
        } catch (error) {
          console.error(
            `[profileSubmitter] Failed to save skill '${skillName}':`,
            error
          );
        }
      }
    }

    console.log("[profileSubmitter] Resume processing results:", results);
    return results;
  } catch (error) {
    console.error("[profileSubmitter] Error processing resume data:", error);
    throw error;
  }
};
